import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardHeader,
    TextField,
    Button,
    Box,
    Alert,
    Collapse,
    IconButton,
    Typography,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import { FaSave, FaTimes, FaEdit, FaCodeBranch } from 'react-icons/fa';
import AiAgentService from '../../../api/AiAgentService';
import VersionSelector from './VersionSelector';
import PromptComparison from './PromptComparison';

const PromptEditor = () => {
    const [promptData, setPromptData] = useState(null);
    const [currentVersion, setCurrentVersion] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [editedTemplate, setEditedTemplate] = useState('');
    const [description, setDescription] = useState('');
    const [authors, setAuthors] = useState([]);
    const [selectedAuthorId, setSelectedAuthorId] = useState(() => {
        // Завантажуємо збереженого автора з localStorage
        const savedAuthorId = localStorage.getItem('promptAuthorId');
        return savedAuthorId ? parseInt(savedAuthorId, 10) : null;
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isVersionChanged, setIsVersionChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [comparisonOpen, setComparisonOpen] = useState(false);

    const loadPromptData = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = {
                versions_page: 1,
                versions_page_size: 1
            };
            
            const response = await AiAgentService.getPromptWithVersions(params);
            setPromptData(response);
            
            // Отримуємо останню версію (першу у списку)
            if (response?.versions && response?.versions?.length > 0) {
                const latestVersion = response.versions[0];
                setCurrentVersion(latestVersion);
                setEditedTemplate(latestVersion.prompt_content || '');
            }

            // Завантажуємо список авторів
            const authorsData = await AiAgentService.getAuthors();
            setAuthors(authorsData || []);
            
            // Якщо є збережений автор, перевіряємо чи він існує в списку
            if (selectedAuthorId && authorsData?.length > 0) {
                const authorExists = authorsData.some(author => author.id === selectedAuthorId);
                if (!authorExists) {
                    // Якщо збереженого автора немає в списку, скидаємо вибір
                    setSelectedAuthorId(null);
                    localStorage.removeItem('promptAuthorId');
                }
            }
        } catch (err) {
            setError('Помилка завантаження промпту: ' + (err.message || 'Невідома помилка'));
        } finally {
            setLoading(false);
        }
    };

    // Завантаження промпту при першому рендері
    useEffect(() => {
        loadPromptData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Обробка зміни версії
    const handleVersionChange = (version) => {
        setSelectedVersion(version);
        setEditedTemplate(version.prompt_content || '');
        setIsVersionChanged(version.id !== currentVersion?.id);
        setIsEditing(false);
        setError('');
        setSuccess('');
    };

    const handleEdit = () => {
        setIsEditing(true);
        setError('');
        setSuccess('');
    };

    const handleCancel = () => {
        setIsEditing(false);
        // Якщо версія була змінена, повертаємо вибрану версію, інакше поточну
        const versionToRestore = selectedVersion || currentVersion;
        setEditedTemplate(versionToRestore?.prompt_content || '');
        setDescription('');
        setError('');
        setSuccess('');
    };

    // Функція для валідації обов'язкових полів у промпті
    const validatePromptTemplate = (template) => {
        const requiredFields = ['{question}', '{context}', '{user_messages}', '{notes}'];
        const missingFields = [];

        requiredFields.forEach(field => {
            if (!template.includes(field)) {
                missingFields.push(field);
            }
        });

        return {
            isValid: missingFields.length === 0,
            missingFields
        };
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            // Валідація опису
            if (!description.trim()) {
                setError('Опис змін є обов\'язковим полем');
                return;
            }

            // Валідація автора
            if (!selectedAuthorId) {
                setError('Оберіть автора зі списку');
                return;
            }

            // Валідація промпту перед збереженням
            const validation = validatePromptTemplate(editedTemplate);
            if (!validation.isValid) {
                setError(`Промпт повинен містити всі обов'язкові поля: ${validation.missingFields.join(', ')}`);
                return;
            }

            // Перевірка чи змінився промпт
            if (editedTemplate === currentVersion?.prompt_content) {
                setError('Промпт не змінився. Внесіть зміни перед збереженням.');
                return;
            }

            // Знаходимо ім'я автора за ID
            const selectedAuthor = authors.find(author => author.id === selectedAuthorId);
            const authorName = selectedAuthor?.name || 'admin';

            const versionData = {
                prompt_id: promptData.prompt.id,
                prompt_content: editedTemplate,
                author: authorName,
                description: description,
                tags: currentVersion?.tags || 'main,agent',
                // Використовуємо commit_hash вибраної версії або поточної
                parent_commit_hash: currentVersion?.commit_hash
            };

            const result = await AiAgentService.createPromptVersion(versionData);

            if (result.success) {
                // Зберігаємо вибраного автора в localStorage
                localStorage.setItem('promptAuthorId', selectedAuthorId.toString());

                // Оновлюємо локальний стан з новою версією
                setCurrentVersion(result.version);
                setSelectedVersion(null);
                setIsVersionChanged(false);
                setPromptData(prev => ({
                    ...prev,
                    versions: [result.version, ...(prev.versions || [])]
                }));

                setIsEditing(false);
                setDescription('');
                
                let successMessage = result.message;
                if (result.warning) {
                    successMessage += ` (${result.warning})`;
                }
                setSuccess(successMessage);

                // Приховуємо повідомлення про успіх через 5 секунд
                setTimeout(() => setSuccess(''), 5000);
            } else {
                setError(result.message || 'Помилка збереження версії промпту');
            }
        } catch (err) {
            setError('Помилка збереження промпту: ' + (err.message || 'Невідома помилка'));
        } finally {
            setSaving(false);
        }
    };

    const hideError = () => {
        setError('');
    };

    const hideSuccess = () => {
        setSuccess('');
    };

    const handleOpenComparison = () => {
        setComparisonOpen(true);
    };

    const handleCloseComparison = () => {
        setComparisonOpen(false);
    };

    if (loading) {
        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader title="Редагування промпту" />
                <CardContent>
                    <Typography>Завантаження...</Typography>
                </CardContent>
            </Card>
        );
    }

    if (!promptData) {
        return (
            <Card sx={{ mb: 3 }}>
                <CardHeader title="Редагування промпту" />
                <CardContent>
                    <Alert severity="error">
                        Не вдалося завантажити дані промпту
                    </Alert>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card sx={{ mt: 3 }}>
            <CardHeader 
                title="Редагування промпту"
                subheader={
                    <Box>
                        <Typography variant="body2">
                            {promptData?.prompt?.name && `Назва: ${promptData.name}`}
                            {promptData?.prompt?.identifier && ` | ID: ${promptData.identifier}`}
                        </Typography>
                        {currentVersion && (
                            <Typography variant="caption" color="text.secondary">
                                Поточна версія: {currentVersion.commit_hash?.substring(0, 8)} | 
                                Автор: {currentVersion.author} | 
                                Створено: {new Date(currentVersion.created_at).toLocaleString('uk-UA')}
                            </Typography>
                        )}
                    </Box>
                }
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {!isEditing ? (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<FaCodeBranch />}
                                    onClick={handleOpenComparison}
                                    disabled={!currentVersion}
                                >
                                    Порівняти
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FaEdit />}
                                    onClick={handleEdit}
                                >
                                    Редагувати
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<FaTimes />}
                                    onClick={handleCancel}
                                    disabled={saving}
                                >
                                    Скасувати
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={<FaSave />}
                                    onClick={handleSave}
                                    disabled={saving || !editedTemplate.trim() || !description.trim() || !selectedAuthorId}
                                >
                                    {saving ? 'Збереження...' : 'Зберегти'}
                                </Button>
                            </>
                        )}
                    </Box>
                }
            />
            <CardContent>
                {/* Селектор версій */}
                {!isEditing && promptData && (
                    <VersionSelector
                        promptId={promptData?.prompt?.id}
                        currentVersion={currentVersion}
                        onVersionChange={handleVersionChange}
                        selectedVersion={selectedVersion}
                    />
                )}

                {/* Повідомлення про зміну версії */}
                {isVersionChanged && !isEditing && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Ви переглядаєте іншу версію промпту. Натисніть "Редагувати" для редагування та збереження цієї версії як нової.
                    </Alert>
                )}
                {/* Повідомлення про помилку */}
                <Collapse in={!!error}>
                    <Alert 
                        severity="error" 
                        sx={{ mb: 2 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={hideError}
                            >
                                <FaTimes fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {error}
                    </Alert>
                </Collapse>

                {/* Повідомлення про успіх */}
                <Collapse in={!!success}>
                    <Alert 
                        severity="success" 
                        sx={{ mb: 2 }}
                        action={
                            <IconButton
                                aria-label="close"
                                color="inherit"
                                size="small"
                                onClick={hideSuccess}
                            >
                                <FaTimes fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {success}
                    </Alert>
                </Collapse>

                {/* Поле опису (тільки при редагуванні) */}
                {isEditing && (
                    <>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel id="author-select-label">Автор *</InputLabel>
                            <Select
                                labelId="author-select-label"
                                id="author-select"
                                value={selectedAuthorId || ''}
                                label="Автор *"
                                onChange={(e) => setSelectedAuthorId(e.target.value)}
                                required
                            >
                                {authors.map((author) => (
                                    <MenuItem key={author.id} value={author.id}>
                                        {author.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            fullWidth
                            label="Опис змін *"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            variant="outlined"
                            sx={{ mb: 2 }}
                            placeholder="Опишіть внесені зміни..."
                            helperText="Опис допоможе відстежувати історію змін промпту"
                            required
                            error={!description.trim() && description !== ''}
                        />
                    </>
                )}

                <TextField
                    fullWidth
                    multiline
                    rows={20}
                    label="Шаблон промпту"
                    value={editedTemplate}
                    onChange={(e) => setEditedTemplate(e.target.value)}
                    InputProps={{
                        readOnly: !isEditing,
                    }}
                    variant="outlined"
                    sx={{ 
                        '& .MuiInputBase-input': {
                            color: 'text.primary',
                            fontFamily: 'monospace',
                        },
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: isEditing ? 'background.paper' : 'grey.50',
                        }
                    }}
                    placeholder="Введіть шаблон промпту..."
                    helperText={isEditing ? "Обов'язково мають бути присутні поля: {question}, {context}, {user_messages}, {notes}" : ""}
                />

                {/* Діалог порівняння версій */}
                {currentVersion && (
                    <PromptComparison
                        open={comparisonOpen}
                        onClose={handleCloseComparison}
                        baseVersion={selectedVersion || currentVersion}
                        promptId={promptData?.prompt?.id}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default PromptEditor;

// console.log('response :>> ', response); повернуло:
// {
//     "success": true,
//     "message": "Промпт успішно отримано",
//     "prompt": {
//         "id": 1,
//         "prompt_name": "default_main_prompt",
//         "prompt_identifier": "test",
//         "created_at": "2025-11-11T11:50:34.436667Z",
//         "updated_at": null,
//         "versions": null
//     },
//     "versions": [
//         {
//             "id": 13,
//             "description": "Оновлено системний промпт",
//             "commit_hash": "9377ca3f11af081c719f759402a43034754b9852a934dd2fa5b256c4ed6291c6",
//             "created_at": "2025-11-11T16:10:37.069622Z",
//             "parent_commit_hash": "87af8add76ea576c4dc26bf9eef0e71da831601284df7818bf7e84042688e985",
//             "prompt_content": "Question: {question}\n\nMessages List: {user_messages}\n\nAI Notes (background, never list as user content): {notes}\n\nRetrieved Context (background): {context}\n",
//             "tags": "main,agent,v2",
//             "langsmith_commit_id": "c5c01c30-1b8d-4fbe-82c0-441ff52743d4",
//             "author": "admin",
//             "prompt_id": 1
//         }
//     ],
//     "versions_total": 11,
//     "versions_page": 1,
//     "versions_page_size": 1,
//     "versions_total_pages": 11
// }